// =============================================================================
// Mock backend for the ui-preview launch profile.
// =============================================================================
//
// Goal: a stable UI preview that doesn't fall over when other agents are
// editing the api/ codebase. Activated only when REACT_APP_USE_MOCKS=true,
// which the `ui-preview` launch profile sets (port 3010). The user's normal
// dev server (port 3000) does NOT set the var, so its behavior is unchanged.
//
// What this does:
//   1. Monkey-patches window.fetch BEFORE the app boots.
//   2. URL patterns we care about return canned JSON shaped like the real API.
//   3. Anything we don't have a handler for falls through to the real fetch
//      (so e.g. webpack HMR, asset loads, etc. keep working).
//   4. /api/v1/protected/* with no handler returns an empty 200 — better than
//      a network error filling up the alerts tray.
//   5. We pre-seed localStorage with a hand-crafted JWT so the app boots
//      "logged in" without a sandbox-token round trip.
//
// Adding a new mock:
//   - Append a `{ method, match, response }` entry to HANDLERS below.
//   - `match` is either a string (substring match) or a RegExp.
//   - `response` returns a plain object (auto-JSON'd) or a full Response.
// =============================================================================

import jwtDecode from "jwt-decode";
import invoiceListFixture from "./fixtures/invoice-list.json";
import invoiceJobFixture from "./fixtures/invoice-job.json";
import invoiceTransactionsFixture from "./fixtures/invoice-transactions.json";
import jobTagsFixture from "./fixtures/job-tags.json";
import invoicesTagLevelFixture from "./fixtures/invoices-taglevel.json";

const REAL_FETCH = window.fetch.bind(window);

const PREVIEW_USER = {
  user: 1,
  email: "preview@docuclipper.com",
  firstName: "Preview",
  lastName: "User",
  picture: "",
  permissions: [
    "MANAGE_TEMPLATES",
    "MANAGE_INTEGRATIONS",
    "READ_JOB_RESULTS",
    "CREATE_JOB",
    "USE_OCR_TOOL",
    "USE_OCR_TABLES_TOOL",
    "USE_TABLES_TOOL",
    "USE_HANDWRITING_TOOL",
  ],
  planName: "Business",
  billingCycle: "Monthly",
  isSandbox: false,
  isContractAdmin: true,
  tv: 0,
  // Page allowance shown in sidebar's CreditSummary (Subscription Pages Left).
  credits: 240,
  purchasedCredits: 0,
};

// Mint an unsigned JWT (signature ignored client-side; jwtDecode just parses
// the payload). Used by getJwt() and Auth reducer on boot.
const buildPreviewJwt = (): string => {
  const b64url = (obj: object) =>
    btoa(JSON.stringify(obj))
      .replace(/=+$/, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
  const header = b64url({ alg: "HS256", typ: "JWT" });
  const payload = b64url({
    ...PREVIEW_USER,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60, // 1 year
  });
  // Signature is not validated client-side. Any base64url string works.
  const sig = "preview-mock-signature";
  return `${header}.${payload}.${sig}`;
};

// =============================================================================
// Mock data
// =============================================================================

// Three Stripe plans matching the shape the upgrade page expects (real plans
// shape — see /api/v1/protected/stripe/plans response). Tiered (multiple
// page-volumes per parent plan) so PlanSelector2's slider has stops.
const PREVIEW_PLANS = (() => {
  const tiers = [
    { name: "Starter", trialNumPages: 50, perSeatPricing: false, pageTiers: [120, 240, 480] },
    { name: "Business", trialNumPages: 120, perSeatPricing: false, pageTiers: [1000, 3000, 6000] },
    { name: "Enterprise", trialNumPages: 500, perSeatPricing: false, pageTiers: [10000, 25000, 50000] },
  ];
  const cyclesAndAmounts = (basePerMonth: number) => [
    { billingCycle: "Monthly", interval: "month", interval_count: 1, monthly: basePerMonth },
    {
      billingCycle: "AnnuallyBilledMonthly",
      interval: "month",
      interval_count: 12,
      monthly: Math.round(basePerMonth * 0.9),
    },
    {
      billingCycle: "Annually",
      interval: "year",
      interval_count: 1,
      monthly: Math.round(basePerMonth * 0.7),
    },
  ];
  // $/month pegs at the smallest page-tier for each parent.
  const baseByParent: Record<string, number> = {
    Starter: 23,
    Business: 153,
    Enterprise: 629,
  };
  const plans: any[] = [];
  for (const t of tiers) {
    for (const numPages of t.pageTiers) {
      // Scale price up with the page tier (rough proportional bump).
      const basePerMonth =
        baseByParent[t.name] *
        (numPages / t.pageTiers[0]);
      for (const c of cyclesAndAmounts(basePerMonth)) {
        const amount = Math.round(c.monthly * 100); // cents
        plans.push({
          docuclipperPlan: {
            name: t.name,
            billingCycle: c.billingCycle,
            numPages,
            perSeatPricing: t.perSeatPricing,
            trialNumPages: t.trialNumPages,
            parentPlan: null,
          },
          amount,
          currency: "usd",
          interval: c.interval,
          interval_count: c.interval_count,
          trialNumPages: t.trialNumPages,
          originalAmount: amount,
        });
      }
    }
  }
  return plans;
})();

const PREVIEW_SUBSCRIPTION = {
  status: "active",
  docuclipperPlan: {
    name: "Starter",
    billingCycle: "Monthly",
    numPages: 240,
    perSeatPricing: false,
    trialNumPages: 50,
  },
  plan: {
    amount: 7400, // R 74.00 / month — matches Starter tier in PREVIEW_PLANS
    currency: "zar",
    interval: "month",
    interval_count: 1,
  },
  currentPeriodEnd: Math.floor(Date.now() / 1000) + 30 * 86400,
  currentPeriodStart: Math.floor(Date.now() / 1000) - 1 * 86400,
  cancelAtPeriodEnd: false,
  cancelAt: null,
  trialEnd: null,
  trialStart: null,
  customer: { id: "cus_preview", default_source: null, sources: { data: [] } },
  lastInvoice: null,
  admin: null,
  seat: null,
  quantity: 1,
  discount: null,
  coupon: null,
  items: { data: [] },
};

// =============================================================================
// Handler table
// =============================================================================

type HandlerResponse =
  | { status?: number; json?: any; body?: string; headers?: Record<string, string> }
  | Response;

type Handler = {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  match: string | RegExp;
  response: (req: { url: string; init?: RequestInit }) => HandlerResponse | Promise<HandlerResponse>;
};

// Stateful overage config for the preview session so the cap UI round-trips
// load → save → load correctly. Mirrors the real Preferences row shape.
let previewOverageConfig: {
  enabled: boolean;
  limitPercent: number | null;
  limitMinorMax: number | null;
  limitCurrency: string | null;
} = { enabled: true, limitPercent: 200, limitMinorMax: null, limitCurrency: null };

// Hard-coded period end so dismiss keys are stable across page reloads in
// preview. Real backend returns the actual subscription period, which is
// stable until renewal anyway.
const PREVIEW_PERIOD_END = 2000000000; // 2033-05-18, well past anything we care about

const HANDLERS: Handler[] = [
  // Auth / user
  {
    match: /\/api\/v1\/protected\/user\/?($|\?)/,
    response: () => ({ json: PREVIEW_USER }),
  },
  // Subscription + plans
  {
    match: "/api/v1/protected/stripe/subscription",
    response: () => ({ json: PREVIEW_SUBSCRIPTION }),
  },
  {
    match: /\/api\/v1\/protected\/stripe\/plans/,
    response: () => ({
      json: {
        plans: PREVIEW_PLANS,
        coupon: null,
        contractAddons: [],
        subscriptionItems: [],
      },
    }),
  },
  // Onboarding
  {
    match: "/api/v1/protected/onboardingSteps",
    response: () => ({
      json: {
        steps: [
          { name: "CONVERT_STATEMENT", completed: true },
          { name: "DOWNLOAD_DATA", completed: true },
          { name: "CATEGORIZE_DATA", completed: true },
          { name: "CONVERT_INVOICE", completed: true },
        ],
      },
    }),
  },
  // Tag levels (folders / projects tree)
  {
    match: /\/api\/v1\/protected\/tagLevel(\?|$)/,
    response: () => ({
      json: {
        tagLevels: {
          root: {
            "Default Folder": {
              "Default Project": {
                metadata: { tagId: 1, badges: {} },
              },
              metadata: { tagId: 2 },
            },
          },
        },
      },
    }),
  },
  // Tag tree (used by fetchTagLevels thunk → setTagTree). getTagTree unwraps
  // `.tagLevels`, then setTagTree(tagTree) writes directly to
  // state.tagLevels.root, so the value of `tagLevels` here IS the root node.
  {
    match: /\/api\/v1\/protected\/tagLevel\/tree/,
    response: () => ({
      json: {
        tagLevels: {
          "Default Folder": {
            "Default Project": {
              metadata: { tagId: 1, badges: {} },
            },
            metadata: { tagId: 2 },
          },
        },
      },
    }),
  },
  // Templates
  {
    match: /\/api\/v1\/protected\/template(\?|$|\/)/,
    response: () => ({
      json: {
        templates: {
          rows: [
            {
              id: 1,
              name: "Sample Receipt Template",
              description: "Demo template — preview only",
              visibility: "private",
              category: "",
              type: "FreeForm",
              email: "preview@docuclipper.com",
            },
          ],
          count: 1,
        },
      },
    }),
  },
  // /job/tags2 — list of tag names. Real fixture so the tags filter
  // dropdown on the invoice list has options to choose from.
  {
    match: /\/api\/v1\/protected\/job\/tags2/,
    response: () => ({ json: jobTagsFixture }),
  },
  // /job/lastUsed — array of recent items.
  {
    match: /\/api\/v1\/protected\/job\/lastUsed/,
    response: () => ({ json: [] }),
  },
  // /job/undefined/invoices — tag-level invoice list (project Invoices tab
  // and the standalone /invoices route both call this with l1/l2 query
  // params). Real fixture: 1 invoice in Default Folder/Default Project.
  // Workflow summary is empty so all-tab/needsApproval/notSynced counts
  // start at 0 (the component refines them when each tab is visited).
  {
    match: /\/api\/v1\/protected\/job\/undefined\/invoices\/workflowSummary/,
    response: () => ({
      json: { byStatus: {}, byApprovalStatus: {}, totalCount: 0 },
    }),
  },
  {
    match: /\/api\/v1\/protected\/job\/undefined\/invoices(\?|$)/,
    response: () => ({ json: invoicesTagLevelFixture }),
  },
  // /job/:id/transactions — invoice/bank line items + documents +
  // validations. Real fixture from a Succeeded invoice job in dev so the
  // InvoicePage / InvoiceViewer renders against realistic data shapes.
  {
    match: /\/api\/v1\/protected\/job\/[^/]+\/transactions/,
    response: () => ({ json: invoiceTransactionsFixture }),
  },
  // /job/:id — single job detail (Invoice viewer reads the job to get
  // metadata + status). Real fixture matched after /transactions above.
  {
    match: /\/api\/v1\/protected\/job\/[^/]+(\?|$)/,
    response: () => ({ json: invoiceJobFixture }),
  },
  // Jobs list — getJobs unwraps `.jobs` from the response, so the body
  // is { jobs: { rows, count } }. Real list fixture has 10 invoice rows.
  {
    match: /\/api\/v1\/protected\/job(\?|$)/,
    response: ({ url }) => {
      // Empty when querying anything other than invoices, so the bank/form
      // tabs show their empty-state cards instead of invoice rows.
      if (url.includes("type=Invoice")) {
        return { json: invoiceListFixture };
      }
      return { json: { jobs: { rows: [], count: 0 } } };
    },
  },
  // Announcements (banner) — none
  {
    match: "/api/v1/protected/announcement",
    response: () => ({ json: null }),
  },
  // Surveys — none pending
  {
    match: "/api/v1/protected/user/pendingSurvey",
    response: () => ({ json: null }),
  },
  // View preferences. The endpoint wraps in { preference: { value: ... } }
  // (loadContractPreferenceServer extracts rsp.preference). uiView is left
  // null on purpose — the flat view renders cleaner than the tree view does
  // with our current mock data set.
  {
    match: /\/api\/v1\/protected\/contractPreference\//,
    response: () => ({ json: { preference: { value: null } } }),
  },
  // Integrations — getIntegrations() unwraps `.integrations`. Same gotcha
  // as getJobs unwrapping `.jobs`. Empty list is enough for now.
  {
    match: /\/api\/v1\/protected\/integration(\?|$)/,
    response: () => ({ json: { integrations: [] } }),
  },
  // Seats / addons / webhooks — empty arrays. (No unwrapping in their
  // callers, plain array is correct.)
  {
    match: /\/api\/v1\/protected\/(seat|addon|webhook)(\?|$|\/)/,
    response: () => ({ json: [] }),
  },
  // Overage status — pretend the preview user is mid-overage so the banner shows.
  // Toggle via window.__previewInOverage = false in console to hide it.
  {
    match: /\/api\/v1\/protected\/stripe\/overageStatus/,
    response: () => {
      const cur = (window as any).__previewCurrency || "zar";
      const inOverage = (window as any).__previewInOverage !== false;
      if (!inOverage) return { json: { inOverage: false, currency: cur } };
      const overagePages = 47;
      const perPageMinor = cur === "zar" ? 150 : 24.17;
      const amountMinor = Math.round(overagePages * perPageMinor);
      const limitPercent = previewOverageConfig.limitPercent;
      const includedPages = 300;
      const maxOveragePages = limitPercent == null ? null : Math.floor((includedPages * limitPercent) / 100);
      return {
        json: {
          inOverage: true,
          enabled: previewOverageConfig.enabled,
          overagePages,
          maxOveragePages,
          amountMinor,
          currency: cur,
          perPageMinor,
          thresholdMinor: cur === "zar" ? 35000 : 1450,
          limitPercent,
          limitMinorMax: previewOverageConfig.limitMinorMax,
          limitCurrency: previewOverageConfig.limitCurrency,
          periodEnd: PREVIEW_PERIOD_END,
        },
      };
    },
  },
  // Overage pricing — returns the user's plan currency so the cap UI renders
  // the right symbol. Pretend the preview user is on a ZAR plan to exercise
  // the non-USD code path. Flip via window.__previewCurrency in console for
  // ad-hoc currency switching when sanity-checking the cap UI.
  {
    match: /\/api\/v1\/protected\/stripe\/overagePricing/,
    response: () => {
      const cur = (window as any).__previewCurrency || "zar";
      const pricePerPage = cur === "zar" ? 1.5 : 0.2417;
      return {
        json: {
          pages: 300,
          pricePerPage,
          currency: cur,
          enabled: true,
          limitPercent: previewOverageConfig.limitPercent,
          limitMinorMax: previewOverageConfig.limitMinorMax,
          limitCurrency: previewOverageConfig.limitCurrency,
          maxAdditionalPages:
            previewOverageConfig.limitPercent == null
              ? null
              : Math.floor((300 * previewOverageConfig.limitPercent) / 100),
        },
      };
    },
  },
  // overageConfig preference — stateful across load/save so the cap UI
  // round-trips correctly within a session.
  {
    match: /\/api\/v1\/protected\/preference\/overageConfig/,
    response: ({ url, init }) => {
      const method = (init?.method || "GET").toUpperCase();
      if (method === "POST" || method === "PUT") {
        try {
          const body = JSON.parse((init?.body as string) || "{}");
          const incoming = body.value ? JSON.parse(body.value) : {};
          // Server-side merge mirror: preserve dollar cap when caller omits it.
          const merged = { ...previewOverageConfig };
          if ("enabled" in incoming) merged.enabled = incoming.enabled;
          if ("limitPercent" in incoming) merged.limitPercent = incoming.limitPercent;
          if ("limitMinorMax" in incoming) merged.limitMinorMax = incoming.limitMinorMax;
          if ("limitCurrency" in incoming) merged.limitCurrency = incoming.limitCurrency;
          // Currency stamp — match real handler: cap currency must match sub.
          if (merged.limitMinorMax != null) {
            const subCurrency = (window as any).__previewCurrency || "zar";
            merged.limitCurrency = subCurrency;
          } else {
            merged.limitCurrency = null;
          }
          previewOverageConfig = merged;
        } catch {
          // ignore malformed payload — keep prior state
        }
      }
      return {
        json: { preference: { key: "overageConfig", value: JSON.stringify(previewOverageConfig) } },
      };
    },
  },
  // Credits balance — used by the Page balance card on Plan & Billing.
  // 193 of 240 plan pages remaining (~80% used) demos the warning bar nicely.
  {
    match: /\/api\/v1\/protected\/credits($|\?)/,
    response: () => ({ json: { credits: 193, purchasedCredits: 50 } }),
  },
  // Automations: input/output configs + processed files (empty list — page
  // renders the "no configs yet" empty state, no crash on .map).
  {
    match: /\/api\/v1\/protected\/tagLevel\/\d+\/input-configs(\?|$|\/)/,
    response: () => ({ json: [] }),
  },
  {
    match: /\/api\/v1\/protected\/tagLevel\/\d+\/output-configs(\?|$|\/)/,
    response: () => ({ json: [] }),
  },
  {
    match: /\/api\/v1\/protected\/tagLevel\/\d+\/processed-files/,
    response: () => ({ json: { files: [], total: 0, offset: 0 } }),
  },
];

// =============================================================================
// Setup
// =============================================================================

const matchHandler = (url: string, method: string): Handler | undefined => {
  for (const h of HANDLERS) {
    if (h.method && h.method !== method) continue;
    if (typeof h.match === "string") {
      if (url.includes(h.match)) return h;
    } else if (h.match.test(url)) {
      return h;
    }
  }
  return undefined;
};

const buildResponse = (resp: HandlerResponse): Response => {
  if (resp instanceof Response) return resp;
  const status = resp.status ?? 200;
  const headers = new Headers({
    "content-type": "application/json",
    ...(resp.headers || {}),
  });
  const body =
    resp.body !== undefined
      ? resp.body
      : resp.json !== undefined
      ? JSON.stringify(resp.json)
      : "";
  return new Response(body, { status, headers });
};

const isProtectedApiCall = (url: string) =>
  /\/api\/v1\/protected\//.test(url);

export const setupMocks = (): void => {
  // Pre-seed JWT (auth reducer reads from localStorage on boot).
  const existing = localStorage.getItem("docuclipper.jwt");
  if (!existing || isExpiredOrSandbox(existing)) {
    localStorage.setItem("docuclipper.jwt", buildPreviewJwt());
  }

  // Capture uncaught errors so the next time we want to debug a screen we
  // can read the real exception out of localStorage instead of fishing
  // through error-boundary noise. Cap at the most recent 10.
  const KEY = "mock_recent_errors";
  const push = (entry: any) => {
    try {
      const existing = JSON.parse(localStorage.getItem(KEY) || "[]");
      existing.unshift({ at: Date.now(), ...entry });
      localStorage.setItem(KEY, JSON.stringify(existing.slice(0, 10)));
    } catch {
      // ignore
    }
  };
  window.addEventListener("error", (e) => {
    push({
      type: "error",
      msg: e.message,
      file: e.filename,
      line: e.lineno,
      col: e.colno,
      stack: (e.error?.stack || "").split("\n").slice(0, 8).join("\n"),
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    push({
      type: "unhandledrejection",
      reason: String(e.reason),
      stack: (e.reason?.stack || "").split("\n").slice(0, 8).join("\n"),
    });
  });

  window.fetch = (async (input: RequestInfo, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input.url;
    const method = (init?.method || "GET").toUpperCase();
    const handler = matchHandler(url, method);
    if (handler) {
      // eslint-disable-next-line no-console
      console.debug("[mocks] →", method, url);
      const resp = await handler.response({ url, init });
      return buildResponse(resp);
    }
    if (isProtectedApiCall(url)) {
      // No handler — return an empty success rather than letting the call
      // hit the network. Prevents the alerts tray from filling with errors
      // when we evaluate a screen we haven't mocked yet.
      // eslint-disable-next-line no-console
      console.debug("[mocks] ☐ unmocked", method, url);
      return buildResponse({ json: {} });
    }
    return REAL_FETCH(input, init);
  }) as typeof fetch;

  // eslint-disable-next-line no-console
  console.info(
    "[mocks] enabled —",
    HANDLERS.length,
    "handlers, JWT pre-seeded for preview@docuclipper.com",
  );
};

const isExpiredOrSandbox = (token: string): boolean => {
  try {
    const claims: any = jwtDecode(token);
    if (claims.isSandbox) return true;
    if (claims.exp && claims.exp < Math.floor(Date.now() / 1000)) return true;
    return false;
  } catch {
    return true;
  }
};
